/** @format */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { Box } from "./MuiCompat";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { BlogPost as BlogPostType } from "../types/blog";
import { blogService } from "../services/blogService";
import BlogPostContent from "./BlogPostContent";

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug header height and selector
  useEffect(() => {
    const appBar = document.querySelector("header.MuiAppBar-root");
    if (appBar) {
      const height = appBar.getBoundingClientRect().height;
      console.log("Header found:", appBar);
      console.log("Header height:", height);
    } else {
      console.log("Header not found with selector 'header.MuiAppBar-root'");
    }
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        const fetchedPost = await blogService.getPostBySlug(slug);

        if (!fetchedPost) {
          setError("Post not found");
          return;
        }

        setPost(fetchedPost);

        // Fetch related posts
        const allPosts = await blogService.getAllPosts();
        const related = allPosts.results
          .filter((p: any) => p.slug !== slug)
          .sort(
            (a: any, b: any) =>
              new Date(b.published_date).getTime() -
              new Date(a.published_date).getTime()
          )
          .slice(0, 4);

        setRelatedPosts(related);
        setError(null);
      } catch (err) {
        setError("Failed to load blog post");
        console.error("Error fetching blog post:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Scroll to top on slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "#fff8f2",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Box sx={{ bgcolor: "#fff8f2", color: "#4a4a4a", minHeight: "100vh" }}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography variant="h4" sx={{ textAlign: "center" }}>
            {error || "Post not found"}
          </Typography>
          <Link to="/blog" style={{ textDecoration: "none" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                color: "#4a4a4a",
                mt: 2,
                "&:hover": { color: "#2a2a2a" },
              }}
            >
              <ArrowBackIcon sx={{ mr: 1, width: 16, height: 16 }} />
              <Typography variant="body2">Back to all articles</Typography>
            </Box>
          </Link>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#fff8f2",
        color: "#4a4a4a",
        minHeight: "100vh",
        pt: { xs: "80px", md: "64px" }, // Fixed padding to account for header
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: "4xl", mx: "auto" }}>
          {/* Article Content */}
          <Box
            component="article"
            sx={{
              maxWidth: { xs: "100%", md: "75%" },
              mx: "auto",
              textAlign: "justify",
            }}
          >
            <Box sx={{ mb: 4, textAlign: "left" }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  fontWeight: "bold",
                  mb: 2,
                  color: "#1a1a1a",
                  fontFamily: "'Georgia', Times, 'Times New Roman', serif",
                }}
              >
                {post.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "#4a4a4a", mb: 2 }}>
                By {post.author.name} | {formatDate(post.published_date)}
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 8,
              }}
            >
              <Box sx={{ overflow: "hidden", borderRadius: "8px" }}>
                <img
                  src={post.thumbnail_url}
                  alt={post.title}
                  style={{
                    width: "100%",
                    maxWidth: 600,
                    maxHeight: 420,
                    objectFit: "contain",
                    objectPosition: "center",
                  }}
                />
              </Box>
            </Box>

            {/* Dynamic Blog Content */}
            <BlogPostContent post={post} />
          </Box>

          {/* Continue Reading Section */}
          {relatedPosts.length > 0 && (
            <Box
              sx={{
                mt: 8,
                pt: 4,
                borderTop: "1px solid",
                borderColor: "grey.400",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  mb: 4,
                  color: "#1a1a1a",
                  fontFamily: "'Georgia', Times, 'Times New Roman', serif",
                }}
              >
                Continue Reading
              </Typography>
              <Grid container spacing={3}>
                {relatedPosts.map((relatedPost) => (
                  <Grid item xs={12} sm={6} key={relatedPost.id}>
                    <Link
                      to={`/blog/${relatedPost.slug}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Card
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          bgcolor: "#333",
                          borderRadius: "8px",
                          overflow: "hidden",
                          transition: "transform 0.3s",
                          "&:hover": {
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
                          <CardMedia
                            component="img"
                            src={relatedPost.thumbnail_url}
                            alt={relatedPost.title}
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              maxHeight: 420,
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                        </Box>
                        <CardContent sx={{ p: 2 }}>
                          <Typography
                            sx={{
                              fontWeight: "medium",
                              color: "#fff",
                              fontSize: "1rem",
                              lineHeight: 1.4,
                            }}
                          >
                            {relatedPost.title}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default BlogPost;